// The tiny markup used for paragraphs and notes (ios Markup.swift): text plus <i>/<em>/<cite> and <b>/<strong>,
// with a few HTML entities. Everything else is dropped.

export interface Run { text: string; italic: boolean; bold: boolean }

export function decode(s: string): string {
  if (!s.includes("&")) return s;
  return s.replaceAll("&lt;", "<").replaceAll("&gt;", ">").replaceAll("&quot;", '"').replaceAll("&#39;", "'")
    .replaceAll("&nbsp;", " ").replaceAll("&amp;", "&");
}

export function escape(s: string): string {
  return s.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

export function runs(s: string): Run[] {
  const out: Run[] = [];
  let italic = 0, bold = 0, buffer = "", i = 0;
  const flush = () => {
    if (!buffer) return;
    out.push({ text: decode(buffer), italic: italic > 0, bold: bold > 0 });
    buffer = "";
  };
  while (i < s.length) {
    const close = s[i] === "<" ? s.indexOf(">", i) : -1;
    if (close >= 0) {
      const tag = s.slice(i + 1, close).toLowerCase().trim();
      const name = tag.split(" ")[0] ?? tag;
      switch (name) {
        case "i": case "em": case "cite": flush(); italic += 1; break;
        case "/i": case "/em": case "/cite": flush(); italic = Math.max(0, italic - 1); break;
        case "b": case "strong": flush(); bold += 1; break;
        case "/b": case "/strong": flush(); bold = Math.max(0, bold - 1); break;
        default: break;
      }
      i = close + 1;
    } else {
      buffer += s[i];
      i += 1;
    }
  }
  flush();
  return out;
}

export function plain(s: string): string {
  return runs(s).map((r) => r.text).join("");
}

const PUNCT_OR_SPACE = /^[\p{P}\s]+|[\p{P}\s]+$/gu;

/** Terms a note wrapped in <b>, so later notes don't define them again. */
export function boldTerms(s: string): string[] {
  return runs(s).filter((r) => r.bold).map((r) => r.text.replace(PUNCT_OR_SPACE, "").toLowerCase());
}

/** Safe HTML for display: only <i>/<b> survive, all text escaped. */
export function toHTML(s: string): string {
  return runs(s).map((r) => {
    let t = escape(r.text);
    if (r.italic) t = `<i>${t}</i>`;
    if (r.bold) t = `<b>${t}</b>`;
    return t;
  }).join("");
}
